import { buttonClasses, createTheme } from '@mui/material';
import type { ButtonProps, Theme } from '@mui/material';
import { BANNER_OFFSET } from './core/bannerOffset';

// Main pages centre their content at this width; narrow single-task pages set their own.
export const PAGE_MAX_WIDTH = 1120;

export const OPACITY = { provisional: 0.5 } as const;

// Buttons, as drawn in Paper › Project Bamboo Design System › Design — buttons.
const WHITE = '#FFFFFF';
const INDIGO_50 = '#EFEFFA';
const RED_50 = '#FFE5E5';
const DISABLED_FILL = 'rgba(8, 8, 27, 0.12)';
const DISABLED_TEXT = 'rgba(8, 8, 27, 0.26)';

type ButtonColor = 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
type ButtonOwnerState = Pick<ButtonProps, 'variant' | 'color'>;

const buttonStyles = ({ theme, ownerState }: { theme: Theme; ownerState: ButtonOwnerState }) => {
  const base = {
    minHeight: 36,
    padding: ownerState.variant === 'text' ? '0 8px' : '0 16px',
    borderRadius: 8,
    textTransform: 'none',
    fontSize: 16,
    fontWeight: 600,
    lineHeight: '24px',
    letterSpacing: '0.5px',
    boxShadow: 'none',
  } as const;
  // color="inherit" buttons sit on dark or coloured surfaces and take their colour from there.
  if (ownerState.color === 'inherit') return base;

  const color = theme.palette[(ownerState.color ?? 'primary') as ButtonColor];
  const contained = ownerState.variant === 'contained';
  const outlined = ownerState.variant === 'outlined';
  const restFill = contained ? color.main : outlined ? WHITE : 'transparent';
  return {
    ...base,
    backgroundColor: restFill,
    color: contained ? color.contrastText : color.main,
    // MUI's outlined style sets a half-strength border after this block; the class selector outranks it.
    [`&.${buttonClasses.outlined}`]: { border: `1px solid ${color.main}` },
    '&:hover': {
      backgroundColor: contained ? color.dark : ownerState.color === 'error' ? RED_50 : INDIGO_50,
      boxShadow: 'none',
      ...(outlined && { borderColor: color.main }),
    },
    [`&.${buttonClasses.focusVisible}`]: { boxShadow: `0 0 0 2px ${WHITE}, 0 0 0 4px ${color.main}` },
    [`&.${buttonClasses.disabled}`]: {
      backgroundColor: contained ? DISABLED_FILL : restFill,
      color: DISABLED_TEXT,
      ...(outlined && { borderColor: DISABLED_FILL }),
    },
    // A loading button keeps its colours and swaps its label for a spinner.
    [`&.${buttonClasses.loading}`]: { backgroundColor: restFill, color: 'transparent', ...(outlined && { borderColor: color.main }) },
  };
};

const loadingIndicatorStyles = ({ theme, ownerState }: { theme: Theme; ownerState: ButtonOwnerState }) => {
  if (ownerState.color === 'inherit') return {};
  const color = theme.palette[(ownerState.color ?? 'primary') as ButtonColor];
  return { color: ownerState.variant === 'contained' ? color.contrastText : color.main };
};

export const theme = createTheme({
  shape: { borderRadius: 10 },
  palette: {
    primary: {
      main: '#2A2A8B',
      light: '#8181D9',
      dark: '#151547',
      contrastText: '#FFFFFF',
    },
    // Red-700: white text on it passes contrast. Danger buttons and error states use it.
    error: {
      main: '#B20101',
      dark: '#890101',
      contrastText: '#FFFFFF',
    },
  },
  typography: {
    fontFamily:
      '"Nunito Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
    fontWeightRegular: 400,
    fontWeightMedium: 600,
    fontWeightBold: 700,
  },
  components: {
    // Side panels and dialogs start below the impersonation banner, so it never
    // covers their headers or footer buttons. The offset is 0px otherwise.
    MuiDrawer: {
      styleOverrides: {
        paperAnchorLeft: { top: BANNER_OFFSET, height: `calc(100% - ${BANNER_OFFSET})` },
        paperAnchorRight: { top: BANNER_OFFSET, height: `calc(100% - ${BANNER_OFFSET})` },
      },
    },
    MuiDialog: {
      styleOverrides: {
        root: { top: BANNER_OFFSET },
      },
    },
    // Every <Button> takes the Bamboo look from here; pick it with variant, size and color.
    MuiButton: {
      defaultProps: { disableElevation: true, disableFocusRipple: true },
      styleOverrides: {
        root: buttonStyles,
        loadingIndicator: loadingIndicatorStyles,
        sizeSmall: { minHeight: 32 },
        sizeLarge: { minHeight: 40 },
      },
    },
  },
});
